import { useState } from 'react';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import NeedsApprovalWorkerTable from './NeedsApprovalWorkerTable';

import { NeedsApprovalShift } from '../api/types';
import { cardActivation } from '../util/cardActivation';
import { formatShiftDateTime } from '../util/dateFormat';

// One needs-approval row — one SHIFT with a pending decision: Date & time ·
// Client · Skill · Location · the Hired fraction, and a chevron that opens the
// Shift Detail panel for that occurrence.
//
// The row used to be per job, with three heuristic fraction sub-states (Hired /
// For Hired / For approval). Product settled it: rows are per shift and the
// label is always "Hired", hired workers on this occurrence over its slots
// (GRAVY-10301).
//
// The chevron used to be a <Link> to /orders/:order_id/jobs/:job_id, which
// dropped the admin out of Dashboard V2 onto the legacy job page and lost the
// queue entirely — GRAVY-10223 requires it to expand into Shift Detail
// (GRAVY-10296). It is a <button> now: it performs an in-page action, so it was
// never a link.

type Props = {
  shift: NeedsApprovalShift;
  /** Opens the Shift Detail panel (card body click). Omitted in isolated tests. */
  onSelect?: (shiftId: number) => void;
  /**
   * Opens the per-worker Review modal (GRAVY-10366). The chevron no longer
   * opens the panel — it expands the roster inline, per D1.
   */
  onReview?: (timesheetId: number, shift: NeedsApprovalShift) => void;
};

const NeedsApprovalRow = ({ shift, onSelect, onReview }: Props) => {
  // Open on load (GRAVY-10838). Ops read check-in/check-out/hours/Review for
  // every worker on the queue, so a collapsed default cost one click per row
  // before the list said anything useful. The chevron still collapses a row.
  const [expanded, setExpanded] = useState(true);
  const tz = shift.timezone;
  // Always present now that rows are shift-based.
  const shiftId = shift.shift_id;
  const workers = shift.workers ?? [];

  // Server-derived. Falling back to the fraction keeps a row from a graphy that
  // predates GRAVY-10366 rendering as "Needs review" with no subtext.
  const needsReview = shift.status === 'needs_review';
  const subtext =
    shift.status_subtext ?? `${shift.hired_count}/${shift.quantity}`;

  const col = (label: string, value: string, modifier: string) => (
    <div className={`dashv2-nscol dashv2-nacol--${modifier}`}>
      <p className="dashv2-nslabel">{label}</p>
      <p className="dashv2-nsvalue" title={value}>
        {value || '—'}
      </p>
    </div>
  );

  return (
    <div
      className="dashv2-nscard"
      {...cardActivation(
        onSelect && shiftId != null ? () => onSelect(shiftId) : undefined,
      )}
    >
      {/* Keyed by shift, not job: a multi-shift job now yields several rows, so
          a job-based id would be duplicated across them. */}
      <div className="dashv2-narow" id={`dashv2-approval-${shift.shift_id}`}>
        {col(
          'Date & time',
          formatShiftDateTime(shift.start_at, shift.end_at, tz),
          'date',
        )}
        {col('Client', shift.client_name || '', 'client')}
        {col('Skill', shift.skill_name || '', 'skill')}
        {col('Location', shift.location_code || '', 'loc')}
        {/* GRAVY-10366 — Status replaces Hired (D1: the Figma is the bar; the
            AC's "Hired fraction … exactly like the current live screen" describes
            the pre-design screen). The fraction is not lost — it IS the subtext
            whenever nothing on the shift is flagged. */}
        <div className="dashv2-nscol dashv2-nacol--fraction">
          <p className="dashv2-nslabel">Status</p>
          <p
            className={`dashv2-nastatus-val ${needsReview ? 'state-review' : 'state-approval'}`}
            data-testid={`dashv2-approval-status-${shift.shift_id}`}
          >
            {needsReview ? 'Needs review' : 'Needs approval'}
          </p>
          <p
            className="dashv2-nastatus-sub"
            data-testid={`dashv2-approval-subtext-${shift.shift_id}`}
          >
            {subtext}
          </p>
        </div>
        <button
          type="button"
          className="dashv2-nsexpand"
          id={`dashv2-approval-chevron-${shift.shift_id}`}
          aria-label={
            expanded ? 'Collapse shift roster' : 'Expand shift roster'
          }
          aria-expanded={expanded}
          onClick={(e) => {
            // Safari does not focus a button on click; focus explicitly so a
            // keyboard user stays on the control they just toggled.
            e.currentTarget.focus();
            setExpanded((open) => !open);
          }}
        >
          {expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
        </button>
      </div>

      {expanded && (
        <NeedsApprovalWorkerTable
          workers={workers}
          timezone={tz}
          onReview={(timesheetId) => onReview?.(timesheetId, shift)}
        />
      )}
    </div>
  );
};

export default NeedsApprovalRow;
